import React, { useCallback, useRef, useState } from "react";
import { useSiteContext } from "../../SiteContext";
import FileDropzone from "../../components/FileDropzone";
import PageWrapper from "../../components/PageWrapper";
import Colors from "../../styles/Colors.json";
import { JobStatus, JobStatusEnum } from "../../interfaces/V2Interface";
import {
  CsvVoucherPieceImportError,
  pollJobUntilTerminal,
  startCsvVoucherPieceImport,
} from "../../api/riceServicesPieceImport";
import "./RiceServicesPieceImport.css";

const CSV_ACCEPT = {
  "text/csv": [".csv"],
  "application/csv": [".csv"],
  "text/plain": [".csv"],
};

const PIECE_IMPORT_COMPLETE_TITLE = "Piece Import Complete";
const PIECE_IMPORT_FAILED_TITLE = "Piece Import Failed";

const PieceImportProgressModal: React.FC<{
  jobStatus: JobStatus | null;
  translator: { translate: (key: string) => string };
}> = ({ jobStatus, translator }) => {
  const progress =
    jobStatus && typeof jobStatus.progress === "number"
      ? Math.max(0, Math.min(100, jobStatus.progress))
      : 0;

  return (
    <div className="piece-import-progress-overlay" role="dialog" aria-modal="true">
      <div className="piece-import-progress-modal">
        {jobStatus ? (
          <div className="progress-container">
            <div className="progress-header">
              <h3>{translator.translate("ProcessingImport")}</h3>
              <div
                className={`job-status-badge job-status-${jobStatus.status.toLowerCase()}`}
              >
                {jobStatus.status}
              </div>
            </div>
            <div className="progress-bar-container">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <span className="progress-text">{progress}%</span>
            </div>
            <p className="current-step">{jobStatus.currentStep || "Processing..."}</p>
          </div>
        ) : (
          <div className="progress-container">
            <h3>{translator.translate("ProcessingImport")}</h3>
            <p className="current-step" style={{ marginTop: 16 }}>
              {translator.translate("StartingImport")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const RiceServicesPieceImport: React.FC = () => {
  const { createPopup, translator } = useSiteContext();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0 || isImporting) return;
      setSelectedFile(acceptedFiles[0]);
      setJobStatus(null);
    },
    [isImporting]
  );

  const handleImport = async () => {
    if (!selectedFile || isImporting) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsImporting(true);
    setJobStatus(null);

    try {
      const { jobID } = await startCsvVoucherPieceImport(selectedFile);

      const terminalJob = await pollJobUntilTerminal(
        jobID,
        (status) => setJobStatus(status),
        3000,
        controller.signal
      );

      const resultData = terminalJob.resultData ?? {};
      const summaryParts = [`Status: ${terminalJob.status}`];
      if (typeof resultData.success === "boolean") {
        summaryParts.push(`Overall success: ${resultData.success}`);
      }
      const json = JSON.stringify(resultData, null, 2);
      const message = `${summaryParts.join("\n")}\n\n${json}`;

      if (terminalJob.status === JobStatusEnum.COMPLETED) {
        createPopup(PIECE_IMPORT_COMPLETE_TITLE, message);
      } else {
        createPopup(PIECE_IMPORT_FAILED_TITLE, message);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      if (error instanceof CsvVoucherPieceImportError && error.status === 403) {
        createPopup(
          "Not Authorized",
          "You are not authorized to import pieces. Contact an administrator for Import Pieces access."
        );
        return;
      }
      const message =
        error instanceof CsvVoucherPieceImportError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Import failed. Please try again.";
      createPopup("Import Failed", message);
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      setIsImporting(false);
      setJobStatus(null);
    }
  };

  return (
    <PageWrapper
      title={translator.translate("riceServicesPieceImport")}
      backgroundColor={Colors.White}
    >
      <FileDropzone
        onDrop={onDrop}
        selectedFile={selectedFile}
        isDragActive={false}
        accept={CSV_ACCEPT}
        placeholderText="Drag & drop a CSV file here, or click to select a file"
      />

      <div className="project-task-display-button-container">
        <button
          type="button"
          className="close-button"
          style={{ marginTop: 24 }}
          disabled={!selectedFile || isImporting}
          onClick={handleImport}
        >
          {translator.translate("Import")}
        </button>
      </div>

      {isImporting && (
        <PieceImportProgressModal jobStatus={jobStatus} translator={translator} />
      )}
    </PageWrapper>
  );
};

export default RiceServicesPieceImport;
